/**
 * Copyright Mántum All Rights Reserved.
 */
import Internal from '../../form/internal/input';
import request from '@mantum/kanela-request';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Search } from 'monolieta-search';
import { source } from '@mantum/kanela';
import { useEffect, useRef } from 'react';

import type { DataSource, Option } from '@mantum/kanela';

type PropTypes = {
    advanced?: {
        entity?: string;
        characters?: number;
        delay?: number;
        settings?: {
            caseSensitive?: boolean;
            exactWordStrategy?: boolean;
            ignoreAccent?: boolean;
            stopWord?: Record<string, boolean>;
            unorderedDocument?: boolean;
        };
    };
    dataSource?: DataSource | Record<string, unknown>[];
    disabled?: boolean;
    name?: string;
    onChange?: (name?: string, value?: Option<unknown> | unknown) => void;
    placeholder?: string;
    testid?: string;
};

export default function SearchBox(props: PropTypes) {
    // prettier-ignore
    const {
        advanced = {}
    } = props;

    const {
        characters = 3,
        delay = 500,
        entity = '',
        settings = {}
    } = advanced;

    const {
        caseSensitive = false,
        exactWordStrategy = false,
        ignoreAccent = true,
        stopWord = {},
        unorderedDocument = true
    } = settings;

    const timeoutRef = useRef<NodeJS.Timeout>();
    const searchRef = useRef<Search | null>(null);

    const isValid = (item?: unknown) => {
        return item && (typeof item === 'string' || typeof item === 'number');
    };

    useEffect(() => {
        searchRef.current = new Search({
            caseSensitive,
            exactWordStrategy,
            ignoreAccent,
            stopWord,
            unorderedDocument
        });

        if (entity && Array.isArray(props.dataSource)) {
            for (let index = 0; index < props.dataSource.length; index++) {
                const element = props.dataSource[index];
                const item = element[entity];
                if (isValid(item)) {
                    searchRef.current?.index(item as string | number, element);
                }
            }
        }
    }, [
        caseSensitive,
        entity,
        exactWordStrategy,
        ignoreAccent,
        stopWord,
        unorderedDocument,
        props.dataSource
    ]);

    const searchInCloud = (value?: unknown) => {
        return new Promise((resolve, reject) => {
            if (!value || typeof value !== 'string') {
                resolve([]);
                return;
            }

            const dataSource = props.dataSource as DataSource;
            const parameter = {
                ...(dataSource.parameter ?? {})
            };

            Object.keys(parameter).forEach((key) => {
                parameter[key] = `${parameter[key]}`.replace('{value}', value);
            });

            const url = dataSource.url.replace('{value}', value);
            const response = request.fetch(url, {
                method: dataSource.method,
                body: parameter,
                headers: dataSource.headers
            });

            response.then((values: unknown[]) => {
                if (!values) {
                    return;
                }

                const results = source.extract<unknown[]>(
                    values,
                    dataSource.namespace
                );

                resolve(results);
            });

            response.catch(reject);
        });
    };

    const searchInLocal = (value?: unknown) => {
        return new Promise((resolve) => {
            if (!value || typeof value !== 'string') {
                resolve([]);
                return;
            }

            const results = [];
            if (entity && Array.isArray(props.dataSource)) {
                const indexes = searchRef.current?.search(value);
                if (indexes) {
                    for (let index = 0; index < indexes.length; index++) {
                        const element = indexes[index];
                        for (let k = 0; k < props.dataSource.length; k++) {
                            const item = props.dataSource[k];
                            const key = item[entity];
                            if (isValid(key) && `${key}` === `${element}`) {
                                results.push(item);
                                continue;
                            }
                        }
                    }
                }
            }

            resolve(results);
        });
    };

    const search = (name?: string, value?: unknown) => {
        return setTimeout(async () => {
            if (props.onChange) {
                const results = Array.isArray(props.dataSource)
                    ? await searchInLocal(value)
                    : await searchInCloud(value);

                props.onChange(name, results);
            }
        }, delay);
    };

    const onChange = (name?: string, value?: unknown) => {
        if (timeoutRef.current !== undefined) {
            clearTimeout(timeoutRef.current);
        }

        if (typeof value === 'string') {
            if (value.length === 0) {
                const dataSource = Array.isArray(props.dataSource)
                    ? props.dataSource
                    : [];

                if (props.onChange) {
                    props.onChange(name, dataSource);
                }
                return;
            }

            if (value.length < characters) {
                return;
            }
        }

        timeoutRef.current = search(name, value);
    };

    return (
        <div className='relative'>
            <span className='absolute my-auto flex h-full flex-col justify-center p-2'>
                <MagnifyingGlassIcon className='h-4' />
            </span>
            <Internal
                {...props}
                className='pl-7'
                onChange={onChange}
                type='text'
            />
        </div>
    );
}
